// src/services/maintenanceScheduler.js
import { getData, saveData } from './storageService';
import { addDays, addMonths, isBefore, format } from 'date-fns';

export class MaintenanceScheduler {
  constructor() {
    this.schedules = [];
    this.maintenanceHistory = [];
    this.loadData();
  }

  async loadData() {
    try {
      this.schedules = await getData('maintenance_schedules') || this.generateDefaultSchedules();
      this.maintenanceHistory = await getData('maintenance_history') || [];
    } catch (error) {
      console.error('Error loading maintenance data:', error);
      this.schedules = this.generateDefaultSchedules();
    }
  }

  async saveData() {
    await saveData('maintenance_schedules', this.schedules);
    await saveData('maintenance_history', this.maintenanceHistory);
  }

  generateDefaultSchedules() {
    return [
      {
        id: 'schedule_001',
        machineId: 'L-12',
        machineName: 'Lavadora L-12',
        type: 'preventive',
        frequency: 'monthly',
        interval: 30, // days
        duration: 120, // minutes
        assignedTo: 'technician_team',
        instructions: [
          'Check water pump and valves',
          'Inspect electrical connections',
          'Lubricate moving parts',
          'Test safety features'
        ],
        nextDue: addDays(new Date(), 15).toISOString(),
        lastPerformed: '2024-01-15',
        status: 'scheduled'
      },
      {
        id: 'schedule_002', 
        machineId: 'S-05',
        machineName: 'Secadora S-05',
        type: 'preventive',
        frequency: 'weekly',
        interval: 7,
        duration: 60,
        assignedTo: 'technician_team',
        instructions: [
          'Clean lint filters',
          'Check heating elements',
          'Inspect ventilation',
          'Test temperature sensors'
        ],
        nextDue: addDays(new Date(), 3).toISOString(),
        lastPerformed: '2024-01-18',
        status: 'urgent'
      }
    ];
  }

  // Автоматичне планування на основі використання
  async generateSmartSchedules(usageData) {
    const newSchedules = [];

    usageData.forEach(machine => {
      const usageIntensity = this.calculateUsageIntensity(machine);
      const recommendedSchedule = this.calculateOptimalSchedule(usageIntensity);
      
      newSchedules.push({
        id: `smart_${machine.id}_${Date.now()}`,
        machineId: machine.id,
        machineName: machine.name,
        type: 'preventive',
        frequency: recommendedSchedule.frequency,
        interval: recommendedSchedule.interval,
        duration: recommendedSchedule.duration,
        assignedTo: 'technician_team',
        instructions: this.generateInstructions(machine.type),
        nextDue: addDays(new Date(), recommendedSchedule.interval).toISOString(),
        lastPerformed: null,
        status: 'scheduled',
        smartGenerated: true
      });
    });

    this.schedules = [...this.schedules, ...newSchedules];
    await this.saveData();
    return newSchedules;
  }

  calculateUsageIntensity(machine) {
    // На основі годин роботи, кількості циклів, etc.
    const hoursUsed = machine.monthlyHours || 160;
    if (hoursUsed > 200) return 'high';
    if (hoursUsed > 120) return 'medium';
    return 'low';
  }

  calculateOptimalSchedule(intensity) {
    const schedules = {
      high: { frequency: 'weekly', interval: 7, duration: 90 },
      medium: { frequency: 'bi-weekly', interval: 14, duration: 60 },
      low: { frequency: 'monthly', interval: 30, duration: 45 }
    };
    return schedules[intensity] || schedules.medium;
  }

  generateInstructions(machineType) {
    const instructions = {
      washer: [
        'Check water pump and valves',
        'Inspect drum bearings',
        'Test water temperature',
        'Clean filters'
      ],
      dryer: [
        'Clean lint filters and ducts',
        'Check heating elements',
        'Inspect ventilation system',
        'Test safety thermostats'
      ],
      ironer: [
        'Check pressure rollers',
        'Inspect heating elements',
        'Lubricate moving parts',
        'Test temperature control'
      ]
    };
    return instructions[machineType] || ['General inspection and maintenance'];
  }

  // Отримати завдання на сьогодні
  getTodaysTasks() {
    const today = new Date().toISOString().split('T')[0];
    return this.schedules.filter(schedule => 
      schedule.nextDue.split('T')[0] === today && schedule.status !== 'completed'
    );
  }

  // Отримати завдання на тиждень
  getWeeklyTasks() {
    const weekFromNow = addDays(new Date(), 7).toISOString();
    return this.schedules.filter(schedule => 
      isBefore(new Date(schedule.nextDue), new Date(weekFromNow)) && 
      schedule.status !== 'completed'
    ).sort((a, b) => new Date(a.nextDue) - new Date(b.nextDue));
  }

  // Отримати прострочені завдання
  getOverdueTasks() {
    const today = new Date();
    return this.schedules.filter(schedule => 
      isBefore(new Date(schedule.nextDue), today) && 
      schedule.status !== 'completed'
    );
  }

  // Позначити ТО як виконане
  async markMaintenanceCompleted(scheduleId, actualDuration, notes = '', photos = []) {
    const schedule = this.schedules.find(s => s.id === scheduleId);
    if (!schedule) return;

    // Оновлюємо розклад
    schedule.status = 'completed';
    schedule.lastPerformed = new Date().toISOString();
    schedule.nextDue = addDays(new Date(), schedule.interval).toISOString();

    // Додаємо в історію
    this.maintenanceHistory.push({
      id: `history_${Date.now()}`,
      scheduleId,
      machineId: schedule.machineId,
      machineName: schedule.machineName,
      performedAt: new Date().toISOString(),
      scheduledDuration: schedule.duration,
      actualDuration,
      notes,
      photos,
      technician: 'current_user'
    });

    await this.saveData();
    return schedule;
  }

  // Статистика ТО
  getMaintenanceStats() {
    const completed = this.schedules.filter(s => s.status === 'completed').length;
    const pending = this.schedules.filter(s => s.status === 'scheduled').length;
    const overdue = this.getOverdueTasks().length;
    const urgent = this.schedules.filter(s => s.status === 'urgent').length;

    return {
      total: this.schedules.length,
      completed,
      pending,
      overdue,
      urgent,
      completionRate: this.schedules.length > 0 ? (completed / this.schedules.length) * 100 : 0
    };
  }
}